import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { usePageTitle } from '@/hooks/usePageTitle';
import {
  Smartphone,
  MessageCircle,
  Bell,
  Clock,
  Users,
  CheckCircle,
  TrendingUp,
  Award,
  Star,
  ChevronLeft,
  ChevronRight,
  Check,
  ClipboardCheck,
  AlertCircle,
  X
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import Header from '@/components/Header';
import Footer from '@/components/Footer';
import SEOHead from '@/components/SEOHead';

const RestaurantStaff = () => {
  // Set SEO-optimized title for Restaurant Staff landing page
  usePageTitle('restaurant-staff');

  const [currentScreen, setCurrentScreen] = useState(0);
  const [isZoomed, setIsZoomed] = useState(false);

  // Placeholder screenshots - replace with actual Dine AI Staff app screenshots
  const appScreenshots = [
    { image: '/images/restaurant-staff/screenshot-1.jpg', alt: 'Dine AI Staff - Order Management Dashboard' },
    { image: '/images/restaurant-staff/screenshot-2.jpg', alt: 'Real-time Notifications - New Orders' },
    { image: '/images/restaurant-staff/screenshot-3.jpg', alt: 'Table Management - Live Status' },
    { image: '/images/restaurant-staff/screenshot-4.jpg', alt: 'Customer Requests - Instant Alerts' },
    { image: '/images/restaurant-staff/screenshot-5.jpg', alt: 'Order Details - Complete Information' }
  ];

  // Auto-rotate screenshots every 8 seconds
  useEffect(() => {
    const timer = setInterval(() => {
      setCurrentScreen((prev) => (prev + 1) % appScreenshots.length);
    }, 8000);
    return () => clearInterval(timer);
  }, []);

  const nextScreen = () => setCurrentScreen((prev) => (prev + 1) % appScreenshots.length);
  const prevScreen = () => setCurrentScreen((prev) => (prev - 1 + appScreenshots.length) % appScreenshots.length);

  const openZoom = () => {
    setIsZoomed(true);
    window.history.pushState({ modal: 'zoom' }, '');
  };

  const closeZoom = () => {
    setIsZoomed(false);
    if (window.history.state?.modal === 'zoom') {
      window.history.back();
    }
  };

  // Handle browser back button
  useEffect(() => {
    const handlePopState = (event: PopStateEvent) => {
      if (isZoomed) {
        event.preventDefault();
        setIsZoomed(false);
      }
    };

    window.addEventListener('popstate', handlePopState);
    return () => window.removeEventListener('popstate', handlePopState);
  }, [isZoomed]);

  // Animation variants
  const fadeInUp = {
    hidden: { opacity: 0, y: 20 },
    visible: {
      opacity: 1,
      y: 0,
      transition: { duration: 0.6 }
    }
  };

  const staggerChildren = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.2
      }
    }
  };

  // Features data
  const features = [
    {
      icon: Bell,
      title: "Real-Time Notifications",
      description: "Get instant alerts for new orders, customer requests, and table updates. Never miss a beat.",
      highlight: "Instant updates",
      color: "text-emerald-400"
    },
    {
      icon: ClipboardCheck,
      title: "Order Management",
      description: "Track order status from kitchen to table. Complete visibility at your fingertips.",
      highlight: "100% organized",
      color: "text-blue-400"
    },
    {
      icon: Users,
      title: "Table Tracking",
      description: "Monitor all tables in real-time. Know exactly what's happening at each table.",
      highlight: "Live status",
      color: "text-purple-400"
    },
    {
      icon: Clock,
      title: "Fast Response",
      description: "Handle customer requests instantly. Respond faster, serve better, earn more tips!",
      highlight: "2x faster service",
      color: "text-dineai-yellow"
    }
  ];

  // Scenarios data
  const scenarios = [
    {
      title: "Busy Friday Night",
      problem: "Managing 10 tables during rush hour",
      solution: "Dine AI Staff keeps you organized with real-time notifications for new orders, special requests, and table updates. No more running back to check order slips!",
      stat: "40% faster service"
    },
    {
      title: "Special Requests",
      problem: "Customer adds 2 more dishes to their order at Table 7",
      solution: "Instant notification to your phone. Update the order immediately without trips back to the kitchen. Customer satisfaction guaranteed.",
      stat: "95% satisfaction"
    },
    {
      title: "New Staff Training",
      problem: "Training new waiters on table management",
      solution: "Simple, intuitive interface that new staff can learn in minutes. Clear order tracking and status updates make training a breeze.",
      stat: "50% less training time"
    }
  ];

  const structuredData = {
    "@context": "https://schema.org",
    "@type": "MobileApplication",
    "name": "Dine AI Staff App",
    "description": "Restaurant staff management app with real-time order tracking, table management, and instant customer request notifications.",
    "applicationCategory": "BusinessApplication",
    "operatingSystem": "iOS, Android",
    "aggregateRating": {
      "@type": "AggregateRating",
      "ratingValue": "4.8",
      "ratingCount": "342"
    },
    "offers": {
      "@type": "Offer",
      "price": "0",
      "priceCurrency": "USD",
      "description": "Free for restaurant staff"
    }
  };

  return (
    <>
      <SEOHead
        title="Dine AI Staff - Restaurant Staff Management App | Real-Time Order Tracking"
        description="Empower your restaurant staff with AI-powered order management, real-time notifications, and table tracking. Download free app for iOS and Android."
        keywords="restaurant staff app, waiter app, order management, table tracking, real-time notifications, restaurant technology"
        structuredData={structuredData}
      />
      <div className="min-h-screen">
        <Header />

      {/* Hero Section - Primary Blue */}
      <section className="pt-32 pb-20 bg-gradient-to-b from-[#1f4ed1] to-[#2256e5] relative overflow-hidden">
        {/* Background elements */}
        <div className="absolute inset-0 pointer-events-none">
          <div className="absolute top-20 left-10 w-96 h-96 rounded-full bg-dineai-yellow/10 blur-3xl"></div>
          <div className="absolute bottom-10 right-20 w-80 h-80 rounded-full bg-white/5 blur-3xl"></div>
        </div>

        <div className="container mx-auto px-4 relative z-10">
          <motion.div
            className="max-w-6xl mx-auto grid md:grid-cols-2 gap-12 items-center"
            initial="hidden"
            animate="visible"
            variants={fadeInUp}
          >
            {/* Left content */}
            <div>
              <motion.div
                className="inline-flex items-center gap-2 bg-white/10 backdrop-blur-sm px-4 py-2 rounded-full mb-6"
                variants={fadeInUp}
              >
                <img src="/images/restaurant-staff/app-icon.png" alt="Dine AI Staff" className="w-6 h-6 rounded-lg" />
                <span className="text-white/90 text-sm font-medium">Dine AI Staff</span>
              </motion.div>

              <motion.h1
                className="text-5xl md:text-6xl font-bold mb-6 text-white"
                variants={fadeInUp}
              >
                Empower Your{" "}
                <span className="bg-gradient-to-r from-dineai-yellow via-yellow-300 to-dineai-yellow bg-clip-text text-transparent whitespace-nowrap">
                  Restaurant Staff
                </span>
              </motion.h1>

              <motion.p
                className="text-xl text-white/80 mb-8"
                variants={fadeInUp}
              >
                Enhance your existing POS with AI-powered customer experience. Real-time order tracking, instant notifications,
                and seamless table management. Every waiter gets the app on their phone—works with Toast, Clover, Square, and more.
              </motion.p>

              {/* App download buttons */}
              <motion.div
                className="flex flex-col sm:flex-row gap-4"
                variants={fadeInUp}
              >
                <a href="https://apps.apple.com/us/app/dine-ai-staff/id6754173000" target="_blank" rel="noopener noreferrer" className="group relative transform transition-all duration-300 hover:scale-105">
                  <div className="backdrop-blur-sm p-4 rounded-xl border border-white/30 shadow-lg group-hover:shadow-xl group-hover:border-white/50 transition-all duration-300" style={{ backgroundColor: 'rgba(50, 86, 220, 0.4)' }}>
                    <img
                      src="/apple-store.png"
                      alt="Download on App Store"
                      className="h-14 w-auto"
                    />
                  </div>
                </a>
                <a href="https://play.google.com/store/apps/details?id=com.dine.ai.staff&utm_source=na_Med" target="_blank" rel="noopener noreferrer" className="group relative transform transition-all duration-300 hover:scale-105">
                  <div className="backdrop-blur-sm p-4 rounded-xl border border-white/30 shadow-lg group-hover:shadow-xl group-hover:border-white/50 transition-all duration-300" style={{ backgroundColor: 'rgba(50, 86, 220, 0.4)' }}>
                    <img
                      src="/google-playstore.png"
                      alt="Get it on Google Play"
                      className="h-14 w-auto"
                    />
                  </div>
                </a>
              </motion.div>

              {/* Stats */}
              <motion.div
                className="flex gap-8 mt-8"
                variants={fadeInUp}
              >
                <div>
                  <div className="text-2xl font-bold text-dineai-yellow">2x Faster</div>
                  <div className="text-sm text-white/70">Service Speed</div>
                </div>
                <div>
                  <div className="text-2xl font-bold text-dineai-yellow">Real-Time</div>
                  <div className="text-sm text-white/70">Notifications</div>
                </div>
                <div>
                  <div className="text-2xl font-bold text-dineai-yellow">95%</div>
                  <div className="text-sm text-white/70">Staff Satisfaction</div>
                </div>
              </motion.div>
            </div>

            {/* Right content - Clean screenshot carousel */}
            <motion.div
              className="relative flex justify-center"
              variants={fadeInUp}
            >
              <div className="relative w-[280px] sm:w-[300px] md:w-[320px]">
                {/* Screenshot carousel - Click to zoom */}
                <div
                  className="relative cursor-pointer group"
                  onClick={openZoom}
                >
                  <div className="relative overflow-hidden rounded-3xl shadow-2xl">
                    <AnimatePresence mode="wait">
                      <motion.img
                        key={currentScreen}
                        src={appScreenshots[currentScreen].image}
                        alt={appScreenshots[currentScreen].alt}
                        initial={{ opacity: 0, x: 100 }}
                        animate={{ opacity: 1, x: 0 }}
                        exit={{ opacity: 0, x: -100 }}
                        transition={{ duration: 0.7 }}
                        className="w-full h-auto object-contain"
                      />
                    </AnimatePresence>
                  </div>

                  {/* Click to zoom hint */}
                  <div className="absolute inset-0 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity duration-300 bg-black/30 rounded-3xl pointer-events-none">
                    <div className="bg-white/90 backdrop-blur-sm px-6 py-3 rounded-full shadow-lg">
                      <p className="text-black font-semibold">
                        Click to zoom
                      </p>
                    </div>
                  </div>
                </div>

                {/* Navigation buttons - subtle */}
                <button
                  onClick={(e) => { e.stopPropagation(); prevScreen(); }}
                  className="absolute -left-4 md:-left-5 top-1/2 -translate-y-1/2 bg-dineai-yellow/70 hover:bg-dineai-yellow text-black p-2 rounded-full shadow-md transition-all hover:scale-105"
                  aria-label="Previous screen"
                >
                  <ChevronLeft className="w-4 h-4" />
                </button>
                <button
                  onClick={(e) => { e.stopPropagation(); nextScreen(); }}
                  className="absolute -right-4 md:-right-5 top-1/2 -translate-y-1/2 bg-dineai-yellow/70 hover:bg-dineai-yellow text-black p-2 rounded-full shadow-md transition-all hover:scale-105"
                  aria-label="Next screen"
                >
                  <ChevronRight className="w-4 h-4" />
                </button>

                {/* Dots indicator */}
                <div className="flex justify-center gap-2 mt-8">
                  {appScreenshots.map((_, index) => (
                    <button
                      key={index}
                      onClick={() => setCurrentScreen(index)}
                      className={`h-2.5 rounded-full transition-all ${
                        index === currentScreen
                          ? 'w-10 bg-dineai-yellow'
                          : 'w-2.5 bg-white/30 hover:bg-white/50'
                      }`}
                      aria-label={`Go to screen ${index + 1}`}
                    />
                  ))}
                </div>
              </div>
            </motion.div>
          </motion.div>
        </div>
      </section>

      {/* Old Way vs New Way Comparison Section */}
      <section className="py-20 bg-[#1a3ba8]">
        <div className="container mx-auto px-4">
          <motion.div
            className="max-w-6xl mx-auto"
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            variants={staggerChildren}
          >
            {/* Section header */}
            <motion.div className="text-center mb-16" variants={fadeInUp}>
              <h2 className="text-4xl md:text-5xl font-bold mb-6 text-white">
                Complete Your{" "}
                <span className="bg-gradient-to-r from-dineai-yellow via-yellow-300 to-dineai-yellow bg-clip-text text-transparent">
                  Restaurant Tech Stack
                </span>
              </h2>
              <p className="text-xl text-white/80 max-w-3xl mx-auto">
                Your POS handles transactions. Dine AI adds the AI-powered customer experience layer.
              </p>
            </motion.div>

            {/* Comparison Grid */}
            <div className="grid md:grid-cols-2 gap-8">
              {/* Your POS System - Left Side */}
              <motion.div
                className="bg-white/5 backdrop-blur-sm rounded-2xl p-8 border-2 border-blue-400/30"
                variants={fadeInUp}
              >
                <div className="flex items-center gap-3 mb-6">
                  <div className="bg-blue-400/20 p-3 rounded-xl">
                    <CheckCircle className="w-8 h-8 text-blue-400" />
                  </div>
                  <div>
                    <h3 className="text-2xl font-bold text-white">Your POS System</h3>
                    <p className="text-blue-400 text-sm">Toast, Clover, Square, etc.</p>
                  </div>
                </div>

                {/* Handheld Device Image */}
                <div className="mb-6 flex justify-center items-center h-96">
                  <img
                    src="/images/restaurant-staff/handheld-device.png"
                    alt="POS handheld device for payments"
                    className="w-72 h-auto max-h-full object-contain"
                  />
                </div>

                <div className="space-y-4">
                  <div className="flex items-start gap-3">
                    <CheckCircle className="w-5 h-5 text-blue-400 flex-shrink-0 mt-0.5" />
                    <p className="text-white/80">
                      <span className="text-blue-400 font-bold">Payment processing</span> - secure transactions
                    </p>
                  </div>
                  <div className="flex items-start gap-3">
                    <CheckCircle className="w-5 h-5 text-blue-400 flex-shrink-0 mt-0.5" />
                    <p className="text-white/80">
                      <span className="text-blue-400 font-bold">Inventory management</span> - track stock levels
                    </p>
                  </div>
                  <div className="flex items-start gap-3">
                    <CheckCircle className="w-5 h-5 text-blue-400 flex-shrink-0 mt-0.5" />
                    <p className="text-white/80">
                      <span className="text-blue-400 font-bold">Order routing</span> - send to kitchen/bar
                    </p>
                  </div>
                  <div className="flex items-start gap-3">
                    <CheckCircle className="w-5 h-5 text-blue-400 flex-shrink-0 mt-0.5" />
                    <p className="text-white/80">
                      <span className="text-blue-400 font-bold">Sales reporting</span> - end-of-day analytics
                    </p>
                  </div>
                  <div className="flex items-start gap-3">
                    <CheckCircle className="w-5 h-5 text-blue-400 flex-shrink-0 mt-0.5" />
                    <p className="text-white/80">
                      <span className="text-blue-400 font-bold">Reliable & proven</span> - industry standard
                    </p>
                  </div>
                </div>
              </motion.div>

              {/* Add Dine AI - Right Side */}
              <motion.div
                className="bg-white/5 backdrop-blur-sm rounded-2xl p-8 border-2 border-dineai-yellow/50 relative overflow-hidden"
                variants={fadeInUp}
              >
                {/* Glow effect */}
                <div className="absolute inset-0 bg-gradient-to-br from-dineai-yellow/10 to-transparent pointer-events-none"></div>

                <div className="relative z-10">
                  <div className="flex items-center gap-3 mb-6">
                    <div className="bg-dineai-yellow/20 p-3 rounded-xl">
                      <CheckCircle className="w-8 h-8 text-dineai-yellow" />
                    </div>
                    <div>
                      <h3 className="text-2xl font-bold text-white">Add Dine AI</h3>
                      <p className="text-dineai-yellow text-sm">AI Customer Experience Layer</p>
                    </div>
                  </div>

                  {/* Smartphone App Screenshot */}
                  <div className="mb-6 flex justify-center items-center h-96">
                    <div className="relative">
                      <img
                        src="/images/restaurant-staff/screenshot-1.jpg"
                        alt="Dine AI Staff app on smartphone"
                        className="w-48 h-auto max-h-full object-contain rounded-2xl shadow-2xl border-2 border-dineai-yellow/30"
                      />
                    </div>
                  </div>

                  <div className="space-y-4">
                    <div className="flex items-start gap-3">
                      <CheckCircle className="w-5 h-5 text-dineai-yellow flex-shrink-0 mt-0.5" />
                      <p className="text-white/80">
                        <span className="text-dineai-yellow font-bold">AI recommendations</span> - personalized for guests
                      </p>
                    </div>
                    <div className="flex items-start gap-3">
                      <CheckCircle className="w-5 h-5 text-dineai-yellow flex-shrink-0 mt-0.5" />
                      <p className="text-white/80">
                        <span className="text-dineai-yellow font-bold">Real-time notifications</span> - customer requests
                      </p>
                    </div>
                    <div className="flex items-start gap-3">
                      <CheckCircle className="w-5 h-5 text-dineai-yellow flex-shrink-0 mt-0.5" />
                      <p className="text-white/80">
                        <span className="text-dineai-yellow font-bold">Menu intelligence</span> - insights & analytics
                      </p>
                    </div>
                    <div className="flex items-start gap-3">
                      <CheckCircle className="w-5 h-5 text-dineai-yellow flex-shrink-0 mt-0.5" />
                      <p className="text-white/80">
                        <span className="text-dineai-yellow font-bold">Works with your POS</span> - seamless integration
                      </p>
                    </div>
                    <div className="flex items-start gap-3">
                      <CheckCircle className="w-5 h-5 text-dineai-yellow flex-shrink-0 mt-0.5" />
                      <p className="text-white/80">
                        <span className="text-dineai-yellow font-bold">Free for staff</span> - on their smartphones
                      </p>
                    </div>
                  </div>

                  {/* CTA */}
                  <div className="mt-8 pt-8 border-t border-white/10">
                    <p className="text-center text-white/90 mb-4">
                      Download now. Get started in minutes.
                    </p>
                    <div className="flex flex-col sm:flex-row gap-3 justify-center">
                      <a href="https://apps.apple.com/us/app/dine-ai-staff/id6754173000" target="_blank" rel="noopener noreferrer" className="inline-block">
                        <Button variant="outline" className="bg-white/10 border-dineai-yellow text-white hover:bg-dineai-yellow hover:text-black transition-all">
                          <Smartphone className="w-4 h-4 mr-2" />
                          iOS App
                        </Button>
                      </a>
                      <a href="https://play.google.com/store/apps/details?id=com.dine.ai.staff&utm_source=na_Med" target="_blank" rel="noopener noreferrer" className="inline-block">
                        <Button variant="outline" className="bg-white/10 border-dineai-yellow text-white hover:bg-dineai-yellow hover:text-black transition-all">
                          <Smartphone className="w-4 h-4 mr-2" />
                          Android App
                        </Button>
                      </a>
                    </div>
                  </div>
                </div>
              </motion.div>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Problem/Solution Section - Secondary Blue */}
      <section className="py-20 bg-[#2256e5]">
        <div className="container mx-auto px-4">
          <motion.div
            className="max-w-6xl mx-auto"
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            variants={staggerChildren}
          >
            {/* Section header */}
            <motion.div className="text-center mb-16" variants={fadeInUp}>
              <h2 className="text-4xl md:text-5xl font-bold mb-6 text-white">
                Restaurant Staff{" "}
                <span className="bg-gradient-to-r from-dineai-yellow via-yellow-300 to-dineai-yellow bg-clip-text text-transparent">
                  Challenges
                </span>
              </h2>
              <p className="text-xl text-white/80 max-w-3xl mx-auto">
                Managing tables, tracking orders, and responding to customer requests shouldn't be a juggling act.
              </p>
            </motion.div>

            {/* Problems grid */}
            <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6 mb-16">
              {[
                { emoji: "📋", text: "Too many orders to remember at once" },
                { emoji: "🏃", text: "Running back and forth to check order status" },
                { emoji: "😰", text: "Missing customer requests during busy shifts" },
                { emoji: "⏱️", text: "Wasting time on manual coordination" }
              ].map((problem, index) => (
                <motion.div
                  key={index}
                  className="backdrop-blur-sm rounded-xl p-6 text-center border border-yellow-400/30 shadow-xl hover:shadow-2xl hover:scale-105 transition-all duration-300"
                  style={{ backgroundColor: '#3256dc' }}
                  variants={fadeInUp}
                >
                  <div className="text-5xl mb-4">{problem.emoji}</div>
                  <p className="text-white/80">{problem.text}</p>
                </motion.div>
              ))}
            </div>

            {/* Solution */}
            <motion.div
              className="backdrop-blur-sm rounded-2xl p-8 border border-yellow-400/30 shadow-xl"
              style={{ backgroundColor: '#3256dc' }}
              variants={fadeInUp}
            >
              <div className="flex flex-col md:flex-row items-center gap-8">
                <div className="flex-1">
                  <h3 className="text-3xl font-bold mb-4 text-white">
                    Meet Dine AI Staff,{" "}
                    <span className="text-dineai-yellow">Your Digital Assistant</span>
                  </h3>
                  <p className="text-white/80 text-lg mb-6">
                    Everything you need to manage your tables and orders efficiently. Real-time updates,
                    instant notifications, and complete order visibility - all in one app.
                  </p>
                  <div className="space-y-3">
                    {[
                      "Real-time order status updates",
                      "Instant notifications for customer requests",
                      "Complete table management dashboard",
                      "Seamless kitchen-to-table coordination"
                    ].map((benefit, index) => (
                      <div key={index} className="flex items-center gap-3">
                        <Check className="w-5 h-5 text-dineai-yellow flex-shrink-0" />
                        <span className="text-white/90">{benefit}</span>
                      </div>
                    ))}
                  </div>
                </div>
                <div className="flex-shrink-0">
                  <Smartphone className="w-32 h-32 text-dineai-yellow/30" />
                </div>
              </div>
            </motion.div>
          </motion.div>
        </div>
      </section>

      {/* Use Case Scenarios Section - Primary Blue */}
      <section className="py-20 bg-[#1f4ed1]">
        <div className="container mx-auto px-4">
          <motion.div
            className="max-w-6xl mx-auto"
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            variants={staggerChildren}
          >
            {/* Section header */}
            <motion.div className="text-center mb-16" variants={fadeInUp}>
              <h2 className="text-4xl md:text-5xl font-bold mb-6 text-white">
                Real-World{" "}
                <span className="bg-gradient-to-r from-dineai-yellow via-yellow-300 to-dineai-yellow bg-clip-text text-transparent">
                  Use Cases
                </span>
              </h2>
              <p className="text-xl text-white/80 max-w-3xl mx-auto">
                See how Dine AI Staff transforms everyday restaurant challenges into seamless service.
              </p>
            </motion.div>

            {/* Scenarios */}
            <div className="space-y-12">
              {scenarios.map((scenario, index) => (
                <motion.div
                  key={index}
                  className="bg-[#2256e5] backdrop-blur-sm rounded-2xl p-8 border border-white/20"
                  variants={fadeInUp}
                >
                  <div className="grid md:grid-cols-3 gap-8 items-center">
                    <div>
                      <div className="inline-block bg-dineai-yellow/20 px-3 py-1 rounded-full mb-4">
                        <span className="text-dineai-yellow text-sm font-medium">Use Case #{index + 1}</span>
                      </div>
                      <h3 className="text-2xl font-bold mb-3 text-white">{scenario.title}</h3>
                      <div className="flex items-start gap-3 mb-4">
                        <AlertCircle className="w-5 h-5 text-red-400 flex-shrink-0 mt-1" />
                        <p className="text-white/70">{scenario.problem}</p>
                      </div>
                    </div>

                    <div className="md:col-span-2">
                      <div className="bg-dineai-yellow/10 rounded-xl p-6 border border-dineai-yellow/30">
                        <div className="flex items-start gap-3 mb-4">
                          <CheckCircle className="w-5 h-5 text-dineai-yellow flex-shrink-0 mt-1" />
                          <p className="text-white/90">{scenario.solution}</p>
                        </div>
                        <div className="flex items-center gap-2 pt-4 border-t border-white/10">
                          <TrendingUp className="w-5 h-5 text-emerald-400" />
                          <span className="text-emerald-400 font-semibold">{scenario.stat}</span>
                        </div>
                      </div>
                    </div>
                  </div>
                </motion.div>
              ))}
            </div>
          </motion.div>
        </div>
      </section>

      {/* Core Features Section - Secondary Blue */}
      <section className="py-20 bg-[#2256e5]">
        <div className="container mx-auto px-4">
          <motion.div
            className="max-w-6xl mx-auto"
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            variants={staggerChildren}
          >
            {/* Section header */}
            <motion.div className="text-center mb-16" variants={fadeInUp}>
              <div className="inline-block bg-dineai-yellow/20 px-4 py-2 rounded-full mb-4">
                <span className="text-dineai-yellow font-medium text-sm">CORE FEATURES</span>
              </div>
              <h2 className="text-4xl md:text-5xl font-bold mb-6 text-white">
                Everything Your Staff{" "}
                <span className="bg-gradient-to-r from-dineai-yellow via-yellow-300 to-dineai-yellow bg-clip-text text-transparent">
                  Needs
                </span>
              </h2>
            </motion.div>

            {/* Features grid */}
            <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
              {features.map((feature, index) => (
                <motion.div
                  key={index}
                  className="group"
                  variants={fadeInUp}
                  whileHover={{ y: -5 }}
                >
                  <div className="backdrop-blur-sm rounded-xl p-6 border border-yellow-400/30 shadow-xl hover:shadow-2xl transition-all h-full" style={{ backgroundColor: '#3256dc' }}>
                    <div className="mb-4">
                      <feature.icon className={`w-12 h-12 ${feature.color}`} strokeWidth={1.5} />
                    </div>
                    <h3 className="text-xl font-bold mb-3 text-white">{feature.title}</h3>
                    <p className="text-white/70 mb-4">{feature.description}</p>
                    <div className="pt-4 border-t border-white/10">
                      <div className="text-dineai-yellow font-semibold">{feature.highlight}</div>
                    </div>
                  </div>
                </motion.div>
              ))}
            </div>
          </motion.div>
        </div>
      </section>

      {/* How It Works Section - Primary Blue */}
      <section className="py-20 bg-[#1f4ed1]">
        <div className="container mx-auto px-4">
          <motion.div
            className="max-w-4xl mx-auto"
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            variants={staggerChildren}
          >
            {/* Section header */}
            <motion.div className="text-center mb-16" variants={fadeInUp}>
              <h2 className="text-4xl md:text-5xl font-bold mb-6 text-white">
                Getting Started is{" "}
                <span className="bg-gradient-to-r from-dineai-yellow via-yellow-300 to-dineai-yellow bg-clip-text text-transparent">
                  Simple
                </span>
              </h2>
            </motion.div>

            {/* Steps */}
            <div className="space-y-8">
              {[
                {
                  step: "1",
                  title: "Download the App",
                  description: "Available for iOS and Android. Free for all restaurant staff members.",
                  icon: Smartphone
                },
                {
                  step: "2",
                  title: "Connect to Your Restaurant",
                  description: "Quick setup with your restaurant's unique code. Start receiving orders immediately.",
                  icon: Users
                },
                {
                  step: "3",
                  title: "Deliver Exceptional Service",
                  description: "Real-time updates keep you informed. Respond faster, serve better, earn more!",
                  icon: Award
                }
              ].map((step, index) => (
                <motion.div
                  key={index}
                  className="flex gap-6 items-start"
                  variants={fadeInUp}
                >
                  <div className="flex-shrink-0 w-16 h-16 bg-dineai-yellow rounded-full flex items-center justify-center">
                    <span className="text-2xl font-bold text-black">{step.step}</span>
                  </div>
                  <div className="flex-1">
                    <h3 className="text-2xl font-bold mb-2 text-white">{step.title}</h3>
                    <p className="text-white/80 text-lg">{step.description}</p>
                  </div>
                  <step.icon className="w-8 h-8 text-dineai-yellow/50 flex-shrink-0" />
                </motion.div>
              ))}
            </div>
          </motion.div>
        </div>
      </section>

      {/* Social Proof Section - Secondary Blue */}
      <section className="py-20 bg-[#2256e5]">
        <div className="container mx-auto px-4">
          <motion.div
            className="max-w-6xl mx-auto"
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            variants={staggerChildren}
          >
            {/* Section header */}
            <motion.div className="text-center mb-16" variants={fadeInUp}>
              <h2 className="text-4xl md:text-5xl font-bold mb-6 text-white">
                Loved by{" "}
                <span className="bg-gradient-to-r from-dineai-yellow via-yellow-300 to-dineai-yellow bg-clip-text text-transparent">
                  Restaurant Staff
                </span>
              </h2>
            </motion.div>

            {/* Testimonials grid */}
            <div className="grid md:grid-cols-3 gap-8 mb-16">
              {[
                {
                  name: "Jessica T., Server",
                  text: "This app has completely changed how I work! I can handle twice as many tables now because I always know what's happening.",
                  rating: 5
                },
                {
                  name: "Marcus L., Head Waiter",
                  text: "Training new staff is so much easier with Dine AI Staff. The interface is intuitive and they're productive from day one.",
                  rating: 5
                },
                {
                  name: "Emma S., Server",
                  text: "I love getting instant notifications for customer requests. My tips have increased because I can respond so much faster!",
                  rating: 5
                }
              ].map((testimonial, index) => (
                <motion.div
                  key={index}
                  className="bg-white/10 backdrop-blur-sm rounded-xl p-6 border border-white/20"
                  variants={fadeInUp}
                >
                  <div className="flex mb-4">
                    {[...Array(testimonial.rating)].map((_, i) => (
                      <Star key={i} className="w-5 h-5 text-dineai-yellow fill-current" />
                    ))}
                  </div>
                  <p className="text-white/90 mb-4 italic">"{testimonial.text}"</p>
                  <p className="text-white/70 font-semibold">{testimonial.name}</p>
                </motion.div>
              ))}
            </div>

            {/* Platform stats */}
            <motion.div
              className="bg-white/10 backdrop-blur-sm rounded-2xl p-8 border border-white/20"
              variants={fadeInUp}
            >
              <div className="grid md:grid-cols-3 gap-8 text-center">
                <div>
                  <div className="text-4xl font-bold text-dineai-yellow mb-2">2x Faster</div>
                  <div className="text-white/70">Service Speed</div>
                  <div className="text-sm text-white/50 mt-1">vs traditional methods</div>
                </div>
                <div>
                  <div className="text-4xl font-bold text-dineai-yellow mb-2">95%</div>
                  <div className="text-white/70">Staff Satisfaction</div>
                  <div className="text-sm text-white/50 mt-1">Love using the app</div>
                </div>
                <div>
                  <div className="text-4xl font-bold text-dineai-yellow mb-2">40%</div>
                  <div className="text-white/70">More Tables</div>
                  <div className="text-sm text-white/50 mt-1">Per shift on average</div>
                </div>
              </div>
            </motion.div>
          </motion.div>
        </div>
      </section>

      {/* Final CTA Section - Primary Blue */}
      <section className="py-20 bg-[#1f4ed1]">
        <div className="container mx-auto px-4">
          <motion.div
            className="max-w-4xl mx-auto text-center"
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            variants={fadeInUp}
          >
            <h2 className="text-4xl md:text-5xl font-bold mb-6 text-white">
              Empower Your Staff{" "}
              <span className="bg-gradient-to-r from-dineai-yellow via-yellow-300 to-dineai-yellow bg-clip-text text-transparent">
                Today
              </span>
            </h2>
            <p className="text-xl text-white/80 mb-12 max-w-2xl mx-auto">
              Give your restaurant staff the tools they need to deliver exceptional service.
              Download Dine AI Staff and transform your restaurant operations.
            </p>

            {/* App download buttons */}
            <div className="flex flex-col sm:flex-row gap-6 justify-center items-center mb-12">
              <a href="https://apps.apple.com/us/app/dine-ai-staff/id6754173000" target="_blank" rel="noopener noreferrer" className="group relative transform transition-all duration-300 hover:scale-105">
                <div className="backdrop-blur-sm p-4 rounded-xl border border-white/30 shadow-lg group-hover:shadow-xl group-hover:border-white/50 transition-all duration-300" style={{ backgroundColor: 'rgba(50, 86, 220, 0.4)' }}>
                  <img
                    src="/apple-store.png"
                    alt="Download on App Store"
                    className="h-14 w-auto"
                  />
                </div>
              </a>
              <a href="https://play.google.com/store/apps/details?id=com.dine.ai.staff&utm_source=na_Med" target="_blank" rel="noopener noreferrer" className="group relative transform transition-all duration-300 hover:scale-105">
                <div className="backdrop-blur-sm p-4 rounded-xl border border-white/30 shadow-lg group-hover:shadow-xl group-hover:border-white/50 transition-all duration-300" style={{ backgroundColor: 'rgba(50, 86, 220, 0.4)' }}>
                  <img
                    src="/google-playstore.png"
                    alt="Get it on Google Play"
                    className="h-14 w-auto"
                  />
                </div>
              </a>
            </div>

            {/* QR Code */}
            <div className="inline-block bg-white/10 backdrop-blur-sm rounded-2xl p-8 border border-white/20">
              <div className="flex justify-center mb-4">
                <img src="/images/restaurant-staff/app-icon.png" alt="Dine AI Staff App Icon" className="w-16 h-16 rounded-2xl shadow-lg" />
              </div>
              <p className="text-white/70 mb-4 text-center font-medium">Scan to download</p>
              <div className="w-48 h-48 bg-white rounded-lg mx-auto p-2">
                <img
                  src="/images/qr-codes/qr-code-staff.png"
                  alt="Scan QR Code to Download Dine AI Staff App"
                  className="w-full h-full"
                />
              </div>
            </div>

            {/* Final tagline */}
            <p className="text-white/60 text-sm mt-12">
              Free for restaurant staff. Ask your manager about Dine AI today.
            </p>
          </motion.div>
        </div>
      </section>

      <Footer />

      {/* Zoom Modal */}
      <AnimatePresence>
        {isZoomed && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-50 flex items-center justify-center bg-black/95 backdrop-blur-sm p-4"
            onClick={closeZoom}
          >
            {/* Close button */}
            <button
              onClick={closeZoom}
              className="absolute top-6 right-6 bg-white/10 hover:bg-white/20 text-white p-3 rounded-full transition-all hover:scale-110 z-10"
              aria-label="Close zoom"
            >
              <Check className="w-6 h-6 rotate-45" />
            </button>

            {/* Navigation in zoom view */}
            <button
              onClick={(e) => { e.stopPropagation(); prevScreen(); }}
              className="absolute left-6 top-1/2 -translate-y-1/2 bg-dineai-yellow/90 hover:bg-dineai-yellow text-black p-4 rounded-full shadow-lg transition-all hover:scale-110 z-10"
              aria-label="Previous screen"
            >
              <ChevronLeft className="w-8 h-8" />
            </button>
            <button
              onClick={(e) => { e.stopPropagation(); nextScreen(); }}
              className="absolute right-6 top-1/2 -translate-y-1/2 bg-dineai-yellow/90 hover:bg-dineai-yellow text-black p-4 rounded-full shadow-lg transition-all hover:scale-110 z-10"
              aria-label="Next screen"
            >
              <ChevronRight className="w-8 h-8" />
            </button>

            {/* Zoomed screenshot - Portrait aspect ratio for phone screenshots */}
            <motion.div
              initial={{ scale: 0.9 }}
              animate={{ scale: 1 }}
              exit={{ scale: 0.9 }}
              className="max-w-sm max-h-[85vh] flex items-center justify-center"
              onClick={(e) => e.stopPropagation()}
            >
              <AnimatePresence mode="wait">
                <motion.img
                  key={currentScreen}
                  src={appScreenshots[currentScreen].image}
                  alt={appScreenshots[currentScreen].alt}
                  initial={{ opacity: 0, x: 100 }}
                  animate={{ opacity: 1, x: 0 }}
                  exit={{ opacity: 0, x: -100 }}
                  transition={{ duration: 0.5 }}
                  className="max-w-full max-h-full object-contain rounded-2xl shadow-2xl"
                />
              </AnimatePresence>
            </motion.div>

            {/* Screen indicator */}
            <div className="absolute bottom-8 left-1/2 -translate-x-1/2 bg-white/10 backdrop-blur-sm px-6 py-3 rounded-full">
              <p className="text-white font-medium">
                {currentScreen + 1} / {appScreenshots.length}
              </p>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
    </>
  );
};

export default RestaurantStaff;
